/**
 * Frontière d'erreur de l'application — correctif de la page blanche.
 *
 * <p><b>Défaut constaté en production :</b> l'application n'avait AUCUNE frontière d'erreur.
 * Une exception levée pendant le rendu (ou un chunk `lazy` introuvable) démonte tout l'arbre
 * React et laisse un `<div id="root">` vide. L'utilisateur voit une page blanche, sans texte,
 * sans bouton, sans explication — et n'a aucun moyen de s'en sortir autrement qu'en devinant
 * qu'il faut recharger.
 *
 * <p>Une erreur d'affichage doit être VISIBLE et ACTIONNABLE. C'est le rôle de ce composant :
 * afficher ce qui s'est passé en langage clair, proposer un rechargement et une sortie vers
 * l'accueil.
 *
 * <p><b>Pourquoi un composant de classe :</b> React n'expose pas d'équivalent en hooks pour
 * `componentDidCatch`. C'est la seule forme possible.
 *
 * @module components/errors/AppErrorBoundary
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { isChunkLoadError } from '@/lib/chunkLoadRecovery';

interface AppErrorBoundaryProps {
  children: ReactNode;
  /** Contenu de repli personnalisé (facultatif). */
  fallback?: ReactNode;
}

interface AppErrorBoundaryState {
  error: Error | null;
  /** Vrai si l'erreur vient d'un chunk introuvable (build obsolète). */
  chunkFailure: boolean;
}

export class AppErrorBoundary extends Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = { error: null, chunkFailure: false };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { error, chunkFailure: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Trace en console : sans cela, une erreur rattrapée disparaît complètement des
    // outils de diagnostic. On ne transmet rien à un tiers.
    console.error('[ZiMed] erreur de rendu rattrapée', error, info.componentStack);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  private handleHome = (): void => {
    window.location.assign('/');
  };

  render(): ReactNode {
    const { error, chunkFailure } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    const title = chunkFailure
      ? 'Mise à jour de l’application nécessaire'
      : 'Cet écran n’a pas pu s’afficher';

    const message = chunkFailure
      ? 'Une nouvelle version de ZiMed a été publiée pendant votre visite. Rechargez la page pour continuer là où vous en étiez.'
      : 'Une erreur inattendue a interrompu l’affichage. Vos données ne sont pas perdues : rechargez la page pour reprendre.';

    return (
      <div
        role="alert"
        data-testid="app-error-boundary"
        className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-6 py-12 text-center"
      >
        <div className="w-20 h-20 rounded-full bg-error-50 flex items-center justify-center mb-6">
          <span className="text-3xl font-bold text-error">!</span>
        </div>

        <h1 className="text-lg font-semibold text-gray-900 mb-2">{title}</h1>
        <p className="text-sm text-gray-600 max-w-md mb-6">{message}</p>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={this.handleReload}
            data-testid="app-error-reload"
            className="inline-flex items-center justify-center rounded-lg font-medium bg-primary-500 text-white hover:bg-primary-600 h-11 px-6"
          >
            Recharger la page
          </button>
          <button
            type="button"
            onClick={this.handleHome}
            data-testid="app-error-home"
            className="inline-flex items-center justify-center rounded-lg font-medium border border-gray-300 text-gray-700 hover:bg-gray-100 h-11 px-6"
          >
            Retour à l’accueil
          </button>
        </div>

        {/*
          Le détail technique est replié : il aide au diagnostic sans imposer un message
          incompréhensible au patient.
        */}
        <details className="mt-8 max-w-md text-left">
          <summary className="text-xs text-gray-500 cursor-pointer">
            Détail technique
          </summary>
          <pre className="mt-2 text-xs text-gray-500 whitespace-pre-wrap break-words">
            {error.message}
          </pre>
        </details>
      </div>
    );
  }
}
